import { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../../api/queryClient";
import AppContext from "../../../../AppContext";
import { APP_TITLE } from "../../../../config/constant";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import ListComponent, { StatusBadge } from "../../../common/lists/ListComponent";
import ContainerLoader from "../../../common/loaders/ContainerLoader";
import MainWindowHeader, { CustomSearch } from "../../../common/mainApp/mainWindowHeaders/MainWindowHeader";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.my-defaults-colors-page {
        display: flex;
        flex-direction: column;
    }

    .add-color-row {
        display: flex;
        align-items: flex-end;
        gap: 16px;
        width: 100%;
    }

    .add-color-system-name {
        flex: 0 0 254px;
        min-width: 0;
    }

    .add-color-system-description {
        flex: 1 1 auto;
        min-width: 320px;
    }

    .add-color-button {
        width: 64px;
        height: 37px;
        flex-shrink: 0;
    }

    .table-action-icon {
        width: 16px;
        height: 16px;
        color: var(--text-icons-gray-100);
    }

    @media (max-width: 900px) {
        .add-color-row {
            flex-wrap: wrap;
        }

        .add-color-system-name,
        .add-color-system-description {
            flex-basis: 100%;
        }
    }

    .rdt_Pagination {
        display: none !important;
    }

    .table-structure > div:nth-child(2) {
        height: 24px;
    }
`;
// #endregion

export default function MyDefaultColorsPage() {
    document.title = `${APP_TITLE} - My Default Colors`;

    // #region VARS
    const { isLoadingApp } = useContext(AppContext);
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region STATES
    //----- Header Filters -----
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]);
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null);
    const [filteredPOS, setFilteredPOS] = useState(undefined);
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const filters = {
        filterRetailBrandIDs: selectedRetailBrand ? [selectedRetailBrand?.value] : [],
        filterPOSIDs: filteredPOS?.map(item => item?.value)
        //searchText: searchText
    };

    // #endregion

    // #region LIST COMPONENT VARS
    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const [listNumRows, setListNumRows] = useState(50);
    const listQueryKey = ["MyDefaultColorsList", filters];
    const listColumns = [
        {
            name: "Color name",
            selector: row => row?.colorName ?? "--"
        },
        {
            name: "Color name ID",
            selector: row => row?.colorNameID || "--"
        },
        {
            name: "Colors",
            selector: row => row?.colors || "--"
        },
        {
            name: "Colors count",
            selector: row => row?.colorsCount || "--"
        },
        {
            name: "Issues",
            selector: row => row?.issues || "--"
        },
        {
            name: "Created by",
            selector: row => row?.createdBy || "--"
        },
        {
            name: "Created on",
            selector: row => row?.createdOn || "--"
        },
        {
            name: "Status",
            selector: row => <StatusBadge statusValue={row?.status ?? "--"} colorValue={"#00bb00"} />
        }
    ];

    const mapListData = data => {
        /*
            const COLOR_NAMES_TEST_DATA = [
                {
                    _id: "color-system-2007588",
                    colorName: "Black",
                    colorNameID: "2007588",
                    colors: "[list of colors], [+#]",
                    colorsCount: "11",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-258",
                    colorName: "Rosa",
                    colorNameID: "258",
                    colors: "[list of colors], [+#]",
                    colorsCount: "10",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-1604408",
                    colorName: "Null",
                    colorNameID: "1604408",
                    colors: "[list of colors], [+#]",
                    colorsCount: "10",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-102926",
                    colorName: "Gull",
                    colorNameID: "102926",
                    colors: "[list of colors], [+#]",
                    colorsCount: "8",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-102925",
                    colorName: "Solv",
                    colorNameID: "102925",
                    colors: "[list of colors], [+#]",
                    colorsCount: "10",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-100584",
                    colorName: "Hvit",
                    colorNameID: "100584",
                    colors: "[list of colors], [+#]",
                    colorsCount: "10",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-100583",
                    colorName: "Orange",
                    colorNameID: "100583",
                    colors: "[list of colors], [+#]",
                    colorsCount: "8",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-100582",
                    colorName: "Lilla",
                    colorNameID: "100582",
                    colors: "[list of colors], [+#]",
                    colorsCount: "39",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                },
                {
                    _id: "color-system-100581",
                    colorName: "Gronn",
                    colorNameID: "100581",
                    colors: "[list of colors], [+#]",
                    colorsCount: "17",
                    issues: "-",
                    createdBy: "-",
                    createdOn: "-",
                    status: "Synced"
                }
            ];
        */

        const testData = data?.map(item => {
            return { colorName: item?.name, colorNameID: item?.id, status: "Synced" };
        });
        return testData;
    };
    // #endregion

    // #region METHODS
    const onSearch = value => {
        setSearchValue(value || "");
    };

    const onRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({
                ...item,
                name: item.name,
                value: item._id,
                selected: true
            }));

            setAllPOSList(formFields);
            setFilteredPOS(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRetailBrandChange = selection => {
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onSelectedPOSChanged = (selections, dropdownDataWithSelections) => {
        setFilteredPOS(selections);
        setAllPOSList(dropdownDataWithSelections);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            searchValue,
            onSearch,
            onRefreshAction,
            allRetailBrandsList,
            onRetailBrandChange,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            onFiltersLoaded
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="my-defaults-colors-page">
            <FormContainer title="DEFAULT COLOR DETAILS" showDefaultRightLegends={true}>
                <div className="add-color-row">
                    <div className="add-color-system-name">
                        <FormField
                            type="text"
                            title="Color name"
                            placeholder="Insert color system name"
                            requiredType="disabledSimple"
                            varName="colorName"
                            tooltip="Color name"
                        />
                    </div>

                    <div className="add-color-system-description">
                        <FormField
                            type="text"
                            title="Color description"
                            placeholder="Insert color system description"
                            requiredType="disabledSimple"
                            varName="colorNameDescription"
                            tooltip="Color description"
                        />
                    </div>

                    <BtnLargeSolid
                        className="add-color-button"
                        text="ADD"
                        disabled={true}
                        normalColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverBorderColor="var(--text-icons-orange-500)"
                        disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                        textStyle={{
                            fontSize: "11px",
                            textTransform: "uppercase"
                        }}
                    />
                </div>
            </FormContainer>

            <ContainerLoader
                isLoading={!isLoadingApp && listComponentIsLoading}
                className="w-100"
                isLoadingOverlay={true}
                addBlurFilters={false}>
                <ListComponent
                    className="w-100"
                    tableClassName="expandedRowsCentered"
                    title="DEFAULT COLORS - AS IN POS"
                    columns={listColumns}
                    api={{
                        useBOstageXAPI: true,
                        BOstageXAPIEndpoint: "X/Products/getProductColorNamesFromPOS",
                        formData: {
                            retailBrandID: selectedRetailBrand?.value || "",
                            limit: listNumRows,
                            ...filters
                        },
                        onSuccess: mapListData
                    }}
                    selectableRows={true}
                    showLoader={false}
                    onListLoading={loading => setListComponentIsLoading(loading)}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    requestEnabled={requestsEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    actions={{
                        includeActions: true,
                        editIsVisible: true,
                        deleteIsVisible: true,
                        suspendIsVisible: false,
                        actionColumnWidth: "100px",
                        actionColumnTitle: "Actions"
                    }}
                />
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    searchValue,
    onSearch,
    onRefreshAction,
    allRetailBrandsList,
    onRetailBrandChange,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    onFiltersLoaded
}) {
    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch
                    enabled={false}
                    placeholder="Search"
                    onTextChange={onSearch}
                    searchValue={searchValue}
                    onClearSearch={() => onSearch("")}
                />
            )
        }
    ]);

    props.setControlBarCenterBtns([
        { to: "/products", active: false, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: true, disabled: false, title: "MY DEFAULTS" }
    ]);

    props.setControlBarRightBtns([
        {
            to: "/products/my-defaults/seasons",
            active: false,
            disabled: false,
            title: "Seasons",
            icon: "snowflake-02"
        },
        {
            to: "/products/my-defaults/genders",
            active: false,
            disabled: false,
            title: "Genders",
            icon: "link-05"
        },
        {
            to: "/products/my-defaults/colors",
            active: true,
            disabled: false,
            title: "Colors",
            icon: "palette"
        },
        {
            to: "/products/my-defaults/categories",
            active: false,
            disabled: false,
            title: "Categ",
            icon: "grid-01"
        },
        {
            to: "/products/my-defaults/sizes",
            active: false,
            disabled: false,
            title: "Sizes",
            icon: "ruler"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "SAVE",
                        enabled: false,
                        onClick: undefined
                    },
                    gridEnabled: false,
                    listEnabled: false
                }}
                search={{
                    visible: false
                }}
                onRefreshClick={onRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailBrandsList,
                    onDropdownChange: onRetailBrandChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedRetailBrand?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: false,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all POS",
                        labelAllSelected: "All POS",
                        labelNoneSelected: "--",
                        allItems: allPOSList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                settingsButton={{
                    visible: true,
                    enabled: false
                }}
            />
        ),
        visible: false,
        gridEnabled: false,
        listEnabled: false,
        onRefreshAction
    });
}
// #endregion

// #region SMALL COMPONENTS

// #endregion
